import { useState } from "react";
import { Link } from "react-router-dom";
import { User, Lock, MapPin, Plus, Trash2, Star, Edit3, Loader2, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { track, trackNavClick } from "@/lib/analytics";

const EMPTY_ADDRESS = { name: "", phone: "", address: "", city: "", state: "", pincode: "", country: "India", isDefault: false };

export const ProfilePage = ({ onAuthOpen }) => {
  useSEO({ title: "My Profile", description: "Manage your Annapurna Home Pickles account, saved addresses, and referral code." });
  const { user, profile, loading, firebaseReady } = useAuth();

  if (!firebaseReady) return (
    <Centered><p className="text-[#2E1A12]/70">Firebase not connected.</p></Centered>
  );
  if (loading) return (
    <Centered><Loader2 className="w-6 h-6 animate-spin text-[#5A2D1D]" /></Centered>
  );
  if (!user) return (
    <Centered>
      <User className="w-10 h-10 mx-auto mb-3 text-[#5A2D1D]" />
      <p className="text-[#2E1A12]/70 mb-5">Sign in to view your profile.</p>
      <button onClick={onAuthOpen} className="bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-semibold">
        Sign In
      </button>
    </Centered>
  );

  // Google-only accounts with no password are blocked at the app shell level
  // (SetPasswordGate) before they can reach this page, so anyone here who
  // signed in via Google already has a real password set.
  const isGoogleUser = user.app_metadata?.provider === "google";

  return (
    <div className="min-h-screen bg-[#F5E6CC] py-10 sm:py-14">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">

        {/* Header */}
        <div>
          <div className="brand-ribbon mb-2">My Profile</div>
          <h1 className="font-display text-3xl font-bold text-[#5A2D1D]">Account Settings</h1>
          <p className="text-sm text-[#2E1A12]/60 mt-1">{user.email}</p>
        </div>

        {/* Profile Info */}
        <Section icon={User} title="Profile Info">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <InfoRow label="Name"  value={profile?.name  || "—"} />
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={profile?.phone || "—"} />
            <InfoRow label="Date of Birth" value={profile?.dob || "—"} />
          </div>
          <p className="text-xs text-[#2E1A12]/50 mt-3">To update your name or phone, contact us on WhatsApp.</p>
        </Section>

        {/* Change Password */}
        {isGoogleUser ? (
          <Section icon={Lock} title="Password">
            <div className="flex items-center gap-2 text-sm text-[#556B2F] font-semibold bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-sm px-3 py-2 mb-4">
              <CheckCircle className="w-4 h-4" /> You can sign in with Google or email + password.
            </div>
            <h3 className="font-display text-base font-bold text-[#5A2D1D] mb-3 mt-2">Change Password</h3>
            <ChangePasswordForm />
          </Section>
        ) : (
          <ChangePasswordSection />
        )}

        {/* Addresses */}
        <AddressSection />

        <div className="flex gap-4 pt-2">
          <Link to="/my-orders" onClick={() => trackNavClick("My Orders", "profile_page")} className="text-sm text-[#556B2F] font-semibold hover:underline">My Orders →</Link>
          <Link to="/offers" onClick={() => trackNavClick("Refer & Earn", "profile_page")} className="text-sm text-[#556B2F] font-semibold hover:underline">Refer & Earn →</Link>
        </div>
      </div>
    </div>
  );
};

// ─── Change Password ────────────────────────────────────────────────────────
const ChangePasswordForm = () => {
  const { changePassword } = useAuth();
  const [form, setForm]     = useState({ current: "", next: "", confirm: "" });
  const [show, setShow]     = useState({ current: false, next: false });
  const [saving, setSaving] = useState(false);
  const [done, setDone]     = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6)            { toast.error("New password must be at least 6 characters."); return; }
    if (form.next !== form.confirm)      { toast.error("New passwords do not match."); return; }
    if (form.current === form.next)      { toast.error("New password must differ from the current one."); return; }
    setSaving(true);
    try {
      await changePassword(form.current, form.next);
      track("password_changed");
      setDone(true);
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed successfully.");
    } catch (err) {
      const msg = err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential"
        ? "Current password is incorrect."
        : err?.message || "Could not change password.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {done && (
        <div className="flex items-center gap-2 text-sm text-[#556B2F] font-semibold bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-sm px-3 py-2 mb-4">
          <CheckCircle className="w-4 h-4" /> Password updated successfully.
        </div>
      )}
      <form onSubmit={submit} className="space-y-3 max-w-sm">
        <PwField label="Current Password" value={form.current} onChange={set("current")} show={show.current}
          onToggle={() => setShow((s) => ({ ...s, current: !s.current }))} />
        <PwField label="New Password" value={form.next} onChange={set("next")} show={show.next}
          onToggle={() => setShow((s) => ({ ...s, next: !s.next }))} />
        <PwField label="Confirm New Password" value={form.confirm} onChange={set("confirm")} show={show.next} />
        <button
          type="submit"
          disabled={saving || !form.current || !form.next || !form.confirm}
          className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Update Password
        </button>
      </form>
    </>
  );
};

const ChangePasswordSection = () => (
  <Section icon={Lock} title="Change Password">
    <ChangePasswordForm />
  </Section>
);

// ─── Addresses ──────────────────────────────────────────────────────────────
const AddressSection = () => {
  const { profile, saveAddress, deleteAddress, setDefaultAddress } = useAuth();
  const addresses = profile?.addresses || [];
  const [editing, setEditing]   = useState(null); // null | {} | existing address
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);

  const startAdd  = () => { track("add_new_address_clicked"); setEditing({ ...EMPTY_ADDRESS }); };
  const startEdit = (a) => { track("edit_address_clicked"); setEditing({ ...a }); };
  const cancel    = () => setEditing(null);

  const save = async (addr) => {
    setSaving(true);
    try {
      const isNew = !addr.id;
      await saveAddress(addr);
      track(isNew ? "address_added" : "address_updated", { is_default: !!addr.isDefault });
      setEditing(null);
      toast.success(addr.id ? "Address updated." : "Address saved.");
    } catch (e) {
      toast.error("Could not save address.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeleting(id);
    try {
      await deleteAddress(id);
      track("address_deleted");
      toast.success("Address removed.");
    } catch (e) {
      toast.error("Could not remove address.");
    } finally {
      setDeleting(null);
    }
  };

  const makeDefault = async (id) => {
    try {
      await setDefaultAddress(id);
      track("address_set_default");
    } catch (e) {
      toast.error("Could not update default address.");
    }
  };

  return (
    <Section icon={MapPin} title="Saved Addresses">
      {addresses.length === 0 && !editing && (
        <p className="text-sm text-[#2E1A12]/60 mb-4">No addresses saved yet.</p>
      )}

      <div className="space-y-3 mb-4">
        {addresses.map((a) => (
          <div key={a.id} data-mp-block="" className={`relative bg-[#EFD9B4]/50 border rounded-sm p-4 text-sm ${
            a.isDefault ? "border-[#556B2F]/50" : "border-[#5A2D1D]/15"
          }`}>
            {a.isDefault && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#556B2F]">
                <Star className="w-3 h-3 fill-[#556B2F]" /> Default
              </span>
            )}
            <div className="font-bold text-[#5A2D1D]">{a.name}</div>
            <div className="text-[#2E1A12]/70">{a.phone}</div>
            <div className="text-[#2E1A12]/80 mt-1 whitespace-pre-wrap">{a.address}</div>
            <div className="text-[#2E1A12]/70">{[a.city, a.state, a.pincode].filter(Boolean).join(", ")}</div>
            <div className="text-[#2E1A12]/70">{a.country}</div>

            <div className="flex items-center gap-3 mt-3">
              <button onClick={() => startEdit(a)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5A2D1D] hover:underline">
                <Edit3 className="w-3 h-3" /> Edit
              </button>
              {!a.isDefault && (
                <button onClick={() => makeDefault(a.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#556B2F] hover:underline">
                  <Star className="w-3 h-3" /> Set Default
                </button>
              )}
              <button
                onClick={() => remove(a.id)}
                disabled={deleting === a.id}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50 ml-auto"
              >
                {deleting === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing ? (
        <AddressForm
          initial={editing}
          saving={saving}
          onSave={save}
          onCancel={cancel}
        />
      ) : (
        <button
          onClick={startAdd}
          className="inline-flex items-center gap-2 border-2 border-dashed border-[#5A2D1D]/30 hover:border-[#5A2D1D]/60 text-[#5A2D1D] text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Add New Address
        </button>
      )}
    </Section>
  );
};

const AddressForm = ({ initial, saving, onSave, onCancel }) => {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim() || !form.city.trim() || !form.pincode.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    onSave(form);
  };

  return (
    <form onSubmit={submit} className="bg-[#EFD9B4]/40 border border-[#5A2D1D]/20 rounded-sm p-5 space-y-3">
      <h4 className="font-display font-bold text-[#5A2D1D] text-base">
        {form.id ? "Edit Address" : "New Address"}
      </h4>

      <div className="grid sm:grid-cols-2 gap-3">
        <AddrField label="Full Name *"    value={form.name}    onChange={set("name")} />
        <AddrField label="Phone"          value={form.phone}   onChange={set("phone")} type="tel" />
      </div>
      <AddrField label="Address *" value={form.address} onChange={set("address")} multiline />
      <div className="grid sm:grid-cols-2 gap-3">
        <AddrField label="City *"    value={form.city}    onChange={set("city")} />
        <AddrField label="State *"   value={form.state}   onChange={set("state")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <AddrField label="Pincode *" value={form.pincode} onChange={set("pincode")} />
        <AddrField label="Country"   value={form.country} onChange={set("country")} />
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {form.id ? "Update" : "Save Address"}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-[#2E1A12]/60 hover:text-[#2E1A12] font-semibold">
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Shared UI helpers ───────────────────────────────────────────────────────
const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6">
    <h2 className="font-display text-lg font-bold text-[#5A2D1D] flex items-center gap-2 mb-5">
      <Icon className="w-4 h-4 text-[#556B2F]" /> {title}
    </h2>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-0.5">{label}</div>
    <div className="text-sm text-[#2E1A12] font-medium">{value}</div>
  </div>
);

const PwField = ({ label, value, onChange, show, onToggle }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">{label}</label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required
        className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] pr-10"
      />
      {onToggle && (
        <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50 hover:text-[#5A2D1D]">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      )}
    </div>
  </div>
);

const AddrField = ({ label, value, onChange, type = "text", multiline }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={onChange}
        rows={2}
        className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
      />
    )}
  </div>
);

const Centered = ({ children }) => (
  <div className="min-h-[60vh] grid place-items-center px-4">
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-10 max-w-sm w-full text-center">
      {children}
    </div>
  </div>
);
